import {
    Box,
    Flex,
    Text,
    VStack,
} from '@chakra-ui/react';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
    AddFilterMenu,
    Button,
    ClearFiltersButton,
    FilterBar,
    FilterChip,
    Icon,
    Popover,
    type IconName,
} from '../../ui';
import {
    CheckboxMenuRow,
    CheckboxMenuSearch,
    MenuFooterActions,
    MenuRow,
    SelectionSummaryTooltip,
    checkboxMenuColors,
    checkboxMenuTriggerMetrics,
} from '../../ui/primitives/CheckboxMenu';
import {
    ACTIVITY_DATE_FILTER_LABELS,
    EMPTY_ACTIVITY_FEED_FILTERS,
    hasActiveActivityFeedFilters,
    type ActivityDateFilter,
    type ActivityFeedEventTypeGroup,
    type ActivityFeedEventTypeOption,
    type ActivityFeedFiltersValue,
    type ActivityFeedUserOption,
} from './types';

type FilterCategory = 'eventType' | 'date' | 'user' | 'excludeInternalActors' | 'excludeGenerated';
type OpenState = 'addFilter' | FilterCategory | null;

const FILTER_CATEGORIES: { key: FilterCategory; label: string; icon: IconName }[] = [
    { key: 'eventType', label: 'Event Type', icon: 'tag' },
    { key: 'date', label: 'Date', icon: 'history' },
    { key: 'user', label: 'User', icon: 'user' },
    { key: 'excludeInternalActors', label: 'Exclude Yaxle Admins', icon: 'shieldLock' },
    { key: 'excludeGenerated', label: 'Exclude Yaxle Generated', icon: 'sparkles' },
];

const DATE_OPTIONS = (Object.entries(ACTIVITY_DATE_FILTER_LABELS) as [ActivityDateFilter, string][])
    .filter(([value]) => value !== 'all');

export interface ActivityFeedFiltersProps {
    filters: ActivityFeedFiltersValue;
    onFiltersChange: (filters: ActivityFeedFiltersValue) => void;
    /** Flat event-type list (invoice / inventory). Ignored when `eventTypeGroups` is set. */
    eventTypeOptions?: ActivityFeedEventTypeOption[];
    /** Grouped event-type list (orders). */
    eventTypeGroups?: ActivityFeedEventTypeGroup[];
    users: ActivityFeedUserOption[];
    /** When set, rendered on the same row as Add filter (search left, Add filter right). */
    searchSlot?: ReactNode;
}

const formatMultiChip = (label: string, values: string[]) => {
    if (values.length === 0) return label;
    if (values.length === 1) return `${label}: ${values[0]}`;
    return `${label}: ${values.length} selected`;
};

function clearCategory(
    source: ActivityFeedFiltersValue,
    key: FilterCategory,
): ActivityFeedFiltersValue {
    const next = { ...source };
    if (key === 'eventType') next.eventTypes = [];
    else if (key === 'date') next.dateFilter = 'all';
    else if (key === 'user') next.userIds = [];
    else if (key === 'excludeInternalActors') next.excludeInternalActors = false;
    else next.excludeGenerated = false;
    return next;
}

const ActivityFeedFilters = ({
    filters,
    onFiltersChange,
    eventTypeOptions = [],
    eventTypeGroups,
    users,
    searchSlot,
}: ActivityFeedFiltersProps) => {
    const [openState, setOpenState] = useState<OpenState>(null);
    const [addFilterView, setAddFilterView] = useState<FilterCategory | null>(null);
    const [tempFilters, setTempFilters] = useState<ActivityFeedFiltersValue>(filters);
    const [filterSearch, setFilterSearch] = useState('');
    const [optionSearch, setOptionSearch] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);

    const flatEventOptions = useMemo(() => {
        if (eventTypeGroups?.length) {
            return eventTypeGroups.flatMap((g) => g.items);
        }
        return eventTypeOptions;
    }, [eventTypeGroups, eventTypeOptions]);

    const closePopover = () => {
        setOpenState(null);
        setAddFilterView(null);
        setFilterSearch('');
        setOptionSearch('');
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (!openState) return;
            const target = event.target as HTMLElement;
            if (containerRef.current?.contains(target)) return;
            if (target.closest('[data-filter-popover]')) return;
            closePopover();
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [openState]);

    const isFilterActive = (key: FilterCategory, source: ActivityFeedFiltersValue = filters) => {
        if (key === 'eventType') return source.eventTypes.length > 0;
        if (key === 'date') return source.dateFilter !== 'all';
        if (key === 'user') return source.userIds.length > 0;
        if (key === 'excludeInternalActors') return source.excludeInternalActors;
        return source.excludeGenerated;
    };

    const activeFilters = FILTER_CATEGORIES.filter((cat) => isFilterActive(cat.key));

    const togglePopover = (target: OpenState) => {
        if (openState === target) {
            closePopover();
        } else {
            setTempFilters(filters);
            setOpenState(target);
            setAddFilterView(null);
            setFilterSearch('');
            setOptionSearch('');
        }
    };

    const applyFilters = () => {
        onFiltersChange(tempFilters);
        closePopover();
    };

    const clearFilter = (key: FilterCategory) => {
        onFiltersChange(clearCategory(filters, key));
        if (openState === key) closePopover();
    };

    const clearAllFilters = () => {
        onFiltersChange({ ...EMPTY_ACTIVITY_FEED_FILTERS });
        closePopover();
    };

    const resetTempFilters = () => {
        if (addFilterView) {
            setTempFilters((prev) => clearCategory(prev, addFilterView));
        } else {
            setTempFilters({ ...EMPTY_ACTIVITY_FEED_FILTERS });
        }
    };

    const getChipLabel = (key: FilterCategory) => {
        if (key === 'eventType') {
            const labels = filters.eventTypes.map(
                (v) => flatEventOptions.find((o) => o.value === v)?.label ?? v,
            );
            return formatMultiChip('Event Type', labels);
        }
        if (key === 'date') {
            return `Date: ${ACTIVITY_DATE_FILTER_LABELS[filters.dateFilter]}`;
        }
        if (key === 'user') {
            const labels = filters.userIds.map(
                (id) => users.find((u) => u.id === id)?.label ?? id,
            );
            return formatMultiChip('User', labels);
        }
        if (key === 'excludeInternalActors') return 'Exclude Yaxle Admins';
        return 'Exclude Yaxle Generated';
    };

    const getCategorySummary = (key: FilterCategory): string | undefined => {
        if (!isFilterActive(key)) return undefined;
        if (key === 'excludeInternalActors' || key === 'excludeGenerated') return 'On';
        const label = getChipLabel(key);
        const separator = label.indexOf(': ');
        return separator === -1 ? label : label.slice(separator + 2);
    };

    const getFilterTooltipItems = (key: FilterCategory): string[] => {
        if (key === 'eventType') {
            return filters.eventTypes.map(
                (v) => flatEventOptions.find((o) => o.value === v)?.label ?? v,
            );
        }
        if (key === 'user') {
            return filters.userIds.map(
                (id) => users.find((u) => u.id === id)?.label ?? id,
            );
        }
        return [];
    };

    const filteredCategories = FILTER_CATEGORIES.filter((cat) =>
        !filterSearch || cat.label.toLowerCase().includes(filterSearch.toLowerCase()),
    );

    const toggleEventType = (value: string) => {
        setTempFilters((prev) => {
            const next = prev.eventTypes.includes(value)
                ? prev.eventTypes.filter((v) => v !== value)
                : [...prev.eventTypes, value];
            return { ...prev, eventTypes: next };
        });
    };

    const toggleUser = (id: string) => {
        setTempFilters((prev) => {
            const next = prev.userIds.includes(id)
                ? prev.userIds.filter((v) => v !== id)
                : [...prev.userIds, id];
            return { ...prev, userIds: next };
        });
    };

    const renderFilterOptions = (key: FilterCategory) => {
        if (key === 'excludeInternalActors') {
            return (
                <VStack align="stretch" spacing={0} p="6px">
                    <MenuRow
                        kind="multi"
                        isSelected={tempFilters.excludeInternalActors}
                        onClick={() => setTempFilters((prev) => ({
                            ...prev,
                            excludeInternalActors: !prev.excludeInternalActors,
                        }))}
                    >
                        Exclude Yaxle Admins
                    </MenuRow>
                </VStack>
            );
        }

        if (key === 'excludeGenerated') {
            return (
                <VStack align="stretch" spacing={0} p="6px">
                    <MenuRow
                        kind="multi"
                        isSelected={tempFilters.excludeGenerated}
                        onClick={() => setTempFilters((prev) => ({
                            ...prev,
                            excludeGenerated: !prev.excludeGenerated,
                        }))}
                    >
                        Exclude Yaxle Generated
                    </MenuRow>
                </VStack>
            );
        }

        if (key === 'date') {
            return (
                <VStack align="stretch" spacing={0} p="6px">
                    <MenuRow
                        kind="single"
                        isSelected={tempFilters.dateFilter === 'all'}
                        onClick={() => setTempFilters((prev) => ({ ...prev, dateFilter: 'all' }))}
                    >
                        All time
                    </MenuRow>
                    {DATE_OPTIONS.map(([value, label]) => (
                        <MenuRow
                            key={value}
                            kind="single"
                            isSelected={tempFilters.dateFilter === value}
                            onClick={() => setTempFilters((prev) => ({ ...prev, dateFilter: value }))}
                        >
                            {label}
                        </MenuRow>
                    ))}
                </VStack>
            );
        }

        if (key === 'eventType') {
            const q = optionSearch.trim().toLowerCase();
            const matches = (label: string) => !q || label.toLowerCase().includes(q);

            if (eventTypeGroups?.length) {
                return (
                    <>
                        <CheckboxMenuSearch
                            value={optionSearch}
                            onChange={setOptionSearch}
                            placeholder="Search event types..."
                        />
                        <Box maxH="280px" overflowY="auto" p="6px">
                            {eventTypeGroups.map((group) => {
                                const items = matches(group.title)
                                    ? group.items
                                    : group.items.filter((item) => matches(item.label));
                                if (items.length === 0) return null;
                                return (
                                    <Box key={group.title} mb={2}>
                                        <Text
                                            fontSize="xs"
                                            fontWeight="semibold"
                                            color="fg.muted"
                                            textTransform="uppercase"
                                            px={2}
                                            py={1}
                                        >
                                            {group.title}
                                        </Text>
                                        {items.map((item) => (
                                            <CheckboxMenuRow
                                                key={item.value}
                                                isSelected={tempFilters.eventTypes.includes(item.value)}
                                                onClick={() => toggleEventType(item.value)}
                                            >
                                                {item.label}
                                            </CheckboxMenuRow>
                                        ))}
                                    </Box>
                                );
                            })}
                        </Box>
                    </>
                );
            }

            const filtered = eventTypeOptions.filter((item) => matches(item.label));
            return (
                <>
                    <CheckboxMenuSearch
                        value={optionSearch}
                        onChange={setOptionSearch}
                        placeholder="Search event types..."
                    />
                    <VStack align="stretch" spacing={0} p="6px" maxH="280px" overflowY="auto">
                        {filtered.map((item) => (
                            <CheckboxMenuRow
                                key={item.value}
                                isSelected={tempFilters.eventTypes.includes(item.value)}
                                onClick={() => toggleEventType(item.value)}
                            >
                                {item.label}
                            </CheckboxMenuRow>
                        ))}
                        {filtered.length === 0 && (
                            <Flex px={3} py={3} justify="center">
                                <Text fontSize={checkboxMenuTriggerMetrics.fontSize} color={checkboxMenuColors.muted}>
                                    No matching event types
                                </Text>
                            </Flex>
                        )}
                    </VStack>
                </>
            );
        }

        // user
        const q = optionSearch.trim().toLowerCase();
        const filteredUsers = users.filter((u) => !q || u.label.toLowerCase().includes(q));
        return (
            <>
                <CheckboxMenuSearch
                    value={optionSearch}
                    onChange={setOptionSearch}
                    placeholder="Search users..."
                />
                <VStack align="stretch" spacing={0} p="6px" maxH="280px" overflowY="auto">
                    {filteredUsers.map((user) => (
                        <CheckboxMenuRow
                            key={user.id}
                            isSelected={tempFilters.userIds.includes(user.id)}
                            onClick={() => toggleUser(user.id)}
                        >
                            {user.label}
                        </CheckboxMenuRow>
                    ))}
                    {filteredUsers.length === 0 && (
                        <Flex px={3} py={3} justify="center">
                            <Text fontSize={checkboxMenuTriggerMetrics.fontSize} color={checkboxMenuColors.muted}>
                                {users.length === 0 ? 'No users found' : 'No matching users'}
                            </Text>
                        </Flex>
                    )}
                </VStack>
            </>
        );
    };

    const renderFooter = (showReset: boolean = true) => (
        <MenuFooterActions
            secondaryLabel={showReset ? 'Reset' : 'Cancel'}
            onSecondary={showReset ? resetTempFilters : closePopover}
            onApply={applyFilters}
        />
    );

    const addFilterMenu = (
        <AddFilterMenu
            isOpen={openState === 'addFilter'}
            onToggle={() => togglePopover('addFilter')}
            width={addFilterView ? '280px' : '220px'}
            contentProps={{ 'data-filter-popover': '' }}
        >
            {addFilterView === null ? (
                <>
                    <CheckboxMenuSearch
                        value={filterSearch}
                        onChange={setFilterSearch}
                        placeholder="Search filters..."
                    />
                    <VStack align="stretch" spacing={0} p="6px">
                        {filteredCategories.map((cat) => (
                            <MenuRow
                                key={cat.key}
                                kind="drillIn"
                                summary={getCategorySummary(cat.key)}
                                icon={<Icon name={cat.icon} size="sm" />}
                                onClick={() => {
                                    setAddFilterView(cat.key);
                                    setOptionSearch('');
                                }}
                            >
                                {cat.label}
                            </MenuRow>
                        ))}
                    </VStack>
                    {renderFooter(true)}
                </>
            ) : (
                <>
                    <Box px={2} py={2} borderBottom="1px solid" borderColor="border.subtle">
                        <Button
                            size="xs"
                            variant="ghost"
                            onClick={() => {
                                setAddFilterView(null);
                                setOptionSearch('');
                            }}
                            leftIcon={<Icon name="chevronLeft" size="xs" />}
                            color="fg.muted"
                            fontWeight="medium"
                        >
                            {FILTER_CATEGORIES.find((c) => c.key === addFilterView)?.label}
                        </Button>
                    </Box>
                    {renderFilterOptions(addFilterView)}
                    {renderFooter(true)}
                </>
            )}
        </AddFilterMenu>
    );

    const chipRow = (
        <FilterBar>
            {activeFilters.map((cat) => (
                <Popover
                    key={cat.key}
                    isOpen={openState === cat.key}
                    placement="bottom-start"
                    closeOnBlur={false}
                    width="280px"
                    contentProps={{ 'data-filter-popover': '' }}
                    trigger={(
                        <SelectionSummaryTooltip
                            items={getFilterTooltipItems(cat.key)}
                            isDisabled={openState === cat.key || getFilterTooltipItems(cat.key).length < 2}
                            placement="bottom-start"
                        >
                            <FilterChip
                                icon={<Icon name={cat.icon} size="sm" />}
                                label={getChipLabel(cat.key)}
                                onRemove={() => clearFilter(cat.key)}
                                onClick={() => togglePopover(cat.key)}
                            />
                        </SelectionSummaryTooltip>
                    )}
                >
                    {renderFilterOptions(cat.key)}
                    {renderFooter(false)}
                </Popover>
            ))}
            {!searchSlot && addFilterMenu}
            {hasActiveActivityFeedFilters(filters) && (
                <ClearFiltersButton onClick={clearAllFilters} />
            )}
        </FilterBar>
    );

    return (
        <Box ref={containerRef}>
            {searchSlot ? (
                <VStack align="stretch" spacing={2}>
                    <Flex align="center" gap={2}>
                        <Box flex={1} minW={0}>{searchSlot}</Box>
                        {addFilterMenu}
                    </Flex>
                    {(activeFilters.length > 0 || hasActiveActivityFeedFilters(filters)) && chipRow}
                </VStack>
            ) : (
                chipRow
            )}
        </Box>
    );
};

export default ActivityFeedFilters;
